import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import { PencilSimple } from '@phosphor-icons/react/dist/ssr/PencilSimple';
import { Check } from '@phosphor-icons/react/dist/ssr/Check';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { IconCircleButton } from '../../UISystem/Icons/IconCircleButton';
import CardCameraButton from '../../Connection/variants/text/CardCameraButton';
import HostPhotoChip from './HostPhotoChip';
import { getInitials } from '../../../utils/connectionName';

/**
 * Host card-flow pieces (tiered-exchange Plan 1, Step 7; mocks profile_2–6).
 * The host's exchange happens ON the People list: a waiting card while the
 * QR sits open/closed, then the new person materializes as the top card with
 * inline venue-confirm (✏️ ✓ ✕) and Take-photo prompts. NO group prompt
 * (removed from the live choreography — chips + detail-page grouping remain).
 */

/** profile_2 — "Waiting to connect..." pseudo-card (bypasses the empty state).
 * ✕ dismisses an abandoned scan (device pass 3: an Anon who scans but never
 * enters their name leaves the spinner forever) — the connection itself is
 * untouched, so a late-arriving name still surfaces the normal card. */
export const WaitingCard = ({ onDismiss }) => (
  <div
    style={{
      width: '100%',
      padding: '16px',
      boxSizing: 'border-box',
      backgroundColor: '#F9F9FB',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    }}
  >
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 1.4, ease: 'linear' }}
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        border: '2.5px solid #1C1C1E',
        borderTopColor: 'transparent',
        flexShrink: 0,
      }}
    />
    <div style={{ flex: 1, fontSize: '16px', color: '#000000', textAlign: 'left' }}>
      Waiting to connect...
    </div>
    {onDismiss && (
      <div onClick={onDismiss} style={{ padding: '6px', cursor: 'pointer', display: 'flex' }}>
        <X size={16} color="#C2C2C8" />
      </div>
    )}
  </div>
);

WaitingCard.propTypes = {
  onDismiss: PropTypes.func,
};

/**
 * profile_3/4 — the just-formed connection as the top card, with the inline
 * prompt row for the current stage:
 *   'venue' → "Met at {venue}" + ✓ ✏️ ✕ on the SAME line (user ruling, device
 *             pass 2 2026-08-08: checkmark leads, and no timestamp — they're
 *             confirming the place, the time isn't theirs to confirm yet).
 *             No venue captured → "Where did you meet?" + ✏️ only (user
 *             ruling: nothing to confirm or dismiss, just a door to set it)
 *   'photo' → [📷 Take photo] chip + ✕
 * The name arrives live via context as the peer's share-back lands; until
 * then the card shows "New connection" with an empty avatar.
 */
export const JustFormedCard = ({
  name,
  venueLine,
  stage,
  onVenueEdit,
  onVenueConfirm,
  onVenueSkip,
  onTakePhoto,
  onPhotoTap,
  onPhotoSkip,
  // TEXT variant renders the card pre-name with a "Name" placeholder (lurker
  // cards accepted — epic §9); CARD keeps "New connection" (name-gated there)
  namePlaceholder = 'New connection',
  // TEXT (device pass 1 mock): the camera is a quiet circular icon ON the
  // card — prominent but never a task; no label, no dismiss (the card
  // settles on photo post or when the flow window ends). CARD keeps the
  // labeled chip + ✕ row.
  cameraIconOnly = false,
  // TEXT (device pass 1e: the lingering flow card read as blocked): whole-card
  // tap into the connection detail; the camera stops propagation. CARD omits
  // it — its flow card settles fast and its stage rows are the interaction.
  onCardClick,
}) => {
  return (
    <div
      onClick={onCardClick}
      style={{
        width: '100%',
        padding: '16px',
        boxSizing: 'border-box',
        backgroundColor: '#F9F9FB',
        borderRadius: '20px',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        cursor: onCardClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Grayed avatar — the peer has no photo yet (initials once named) */}
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#E3E3E7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {name ? (
            <span style={{ fontSize: '16px', fontWeight: '500', color: '#9A9AA0' }}>
              {getInitials(name)}
            </span>
          ) : (
            <User size={24} color="#9A9AA0" weight="regular" />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#000',
              lineHeight: '1.2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left', // full-width block — .App centers text globally
            }}
          >
            {name || namePlaceholder}
          </div>

          {(venueLine || stage === 'venue') && (
            <div
              style={{
                marginTop: '3px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '13px',
                  color: '#8E8E93',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'left', // full-width block — .App centers text globally
                }}
              >
                {venueLine || (stage === 'venue' ? 'Where did you meet?' : '')}
              </div>

              {/* Inline venue icons — with a venue: ✓ ✏️ ✕ confirm row;
                  without one: ✏️ alone (nothing to confirm or dismiss) */}
              {stage === 'venue' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  {venueLine && (
                    <IconCircleButton
                      icon={Check}
                      size={18}
                      buttonSize={34}
                      onClick={onVenueConfirm}
                    />
                  )}
                  <IconCircleButton
                    icon={PencilSimple}
                    size={16}
                    buttonSize={34}
                    onClick={onVenueEdit}
                  />
                  {venueLine && (
                    <div
                      onClick={onVenueSkip}
                      style={{ padding: '6px', cursor: 'pointer', display: 'flex' }}
                    >
                      <X size={16} color="#C2C2C8" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Photo prompt row (stage 'photo') — the shared HostPhotoChip
              (no Camera/Library split here; Library lives only in the SP's own
              QR-customize sheet). TEXT renders the icon button instead
              (cameraIconOnly — see the card-level button below). */}
          {stage === 'photo' && !cameraIconOnly && (
            <HostPhotoChip
              onTakePhoto={onTakePhoto}
              onSkip={onPhotoSkip}
              style={{ marginTop: '10px' }}
            />
          )}
        </div>

        {/* TEXT: the camera as a card-borne circular icon — an option, not a
            task (device pass 1 ruling: prominent, never blocking; escalate to
            a labeled chip only on field evidence that photos flatline).
            CardCameraButton owns the icon + capture input (/simplify: this
            block was a line-for-line copy of it) */}
        {stage === 'photo' && cameraIconOnly && (
          <div style={{ alignSelf: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            <CardCameraButton onFile={onTakePhoto} onTap={onPhotoTap} />
          </div>
        )}
      </div>
    </div>
  );
};

JustFormedCard.propTypes = {
  name: PropTypes.string,
  venueLine: PropTypes.string,
  stage: PropTypes.oneOf(['venue', 'photo']).isRequired,
  onVenueEdit: PropTypes.func.isRequired,
  onVenueConfirm: PropTypes.func.isRequired,
  onVenueSkip: PropTypes.func.isRequired,
  onTakePhoto: PropTypes.func.isRequired,
  onPhotoTap: PropTypes.func,
  onPhotoSkip: PropTypes.func.isRequired,
  namePlaceholder: PropTypes.string,
  cameraIconOnly: PropTypes.bool,
  onCardClick: PropTypes.func,
};
